import { Add, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, GreenText, OrderableTableCell, PastDueCell, Title } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getToDoList as getToDoListAPI } from 'controllers/toDoLists/ohioTitles/index'
import { VehicleDto } from 'dtos'
import { NeedsToBeSentOutgoingDto, NeedsToBeSentOutgoingQueryParams } from 'dtos/toDoLists/ohioTitles'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'

export default function NeedsToBeSentOutgoingToDoList() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [isGettingToDoList, setIsGettingToDoList] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<NeedsToBeSentOutgoingQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'createdDateTime' })
  const [toDoList, setToDoList] = useState<NeedsToBeSentOutgoingDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: NeedsToBeSentOutgoingQueryParams) => {
    setIsGettingToDoList(true)
    getToDoListAPI(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        setIsGettingToDoList(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingToDoList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGettingToDoList])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <Title>Ohio Titles Needs To Be Sent Outgoing</Title>

      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created</MenuItem>

                    <MenuItem value='titleUploadedDateTime'>Title Uploaded</MenuItem>

                    <MenuItem value='exporterCompany'>Exporter</MenuItem>

                    <MenuItem value='buyerCompany'>Buyer</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'exporterCompany', 'buyerCompany', 'importerStockNumber', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['createdDateTime', 'titleUploadedDateTime'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField || (['createdDateTime', 'titleUploadedDateTime'].includes(filterField) ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'createdDateTime') {
                        setQueryParams({
                          ...queryParams,
                          createdDateTimeFrom: filterValueFrom,
                          createdDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'titleUploadedDateTime') {
                        setQueryParams({
                          ...queryParams,
                          titleUploadedDateTimeFrom: filterValueFrom,
                          titleUploadedDateTimeTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.createdDateTimeFrom ||
                          queryParams.createdDateTimeTo ||
                          queryParams.titleUploadedDateTimeFrom ||
                          queryParams.titleUploadedDateTimeTo ||
                          queryParams.exporterCompany ||
                          queryParams.buyerCompany ||
                          queryParams.importerStockNumber ||
                          queryParams.vin ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        createdDateTimeFrom: undefined,
                        createdDateTimeTo: undefined,
                        titleUploadedDateTimeFrom: undefined,
                        titleUploadedDateTimeTo: undefined,
                        exporterCompany: undefined,
                        buyerCompany: undefined,
                        importerStockNumber: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.createdDateTimeFrom && queryParams.createdDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created from "${moment(queryParams.createdDateTimeFrom).format('M/D/yyyy')}" to "${moment(queryParams.createdDateTimeTo).format(
                      'M/D/yyyy'
                    )}"`}
                    onDelete={() => setQueryParams({ ...queryParams, createdDateTimeFrom: undefined, createdDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.titleUploadedDateTimeFrom && queryParams.titleUploadedDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Title Uploaded from "${moment(queryParams.titleUploadedDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.titleUploadedDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, titleUploadedDateTimeFrom: undefined, titleUploadedDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.exporterCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${queryParams.exporterCompany}"`}
                    onDelete={() => setQueryParams({ ...queryParams, exporterCompany: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.buyerCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Buyer contains "${queryParams.buyerCompany}"`}
                    onDelete={() => setQueryParams({ ...queryParams, buyerCompany: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${queryParams.vin}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='createdDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Created
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='titleUploadedDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Title Uploaded
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='exporterCompany'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Exporter
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='buyerCompany'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Buyer
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='importerStockNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Importer Stock #
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='vin'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              VIN
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='year'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='make'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsToBeSentOutgoingDto>
                              keyName='model'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsToBeSentOutgoingDto | undefined}
                            >
                              Model
                            </OrderableTableCell>

                            <TableCell padding='checkbox' size='small'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <PastDueCell daysDiff={14}>{item.createdDateTime}</PastDueCell>

                              <PastDueCell daysDiff={1}>{item.titleUploadedDateTime}</PastDueCell>

                              <TableCell>{item.exporterCompany}</TableCell>

                              <TableCell>{item.buyerCompany ?? '-'}</TableCell>

                              <TableCell>{item.importerStockNumber}</TableCell>

                              <TableCell>{item.vin}</TableCell>

                              <TableCell>{item.year}</TableCell>

                              <TableCell>{item.make}</TableCell>

                              <TableCell>{item.model}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => navigate(`/vehicles/${encodeURIComponent(item.vehicleGuid)}/title`)}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Ohio Titles – Needs To Be Sent Outgoing To Do List:**

Vehicles are in this To Do List when Title Application State = Ohio, the vehicle has an Ohio Title document associated with it, but the vehicle is not associated with any Outgoing Titles Group.

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Created,
   - Title Uploaded,
   - Exporter.
   - Buyer,
   - Importer Stock #,
   - VIN,
   - Year,
   - Make,
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Created: Displays Filter Date From and Filter Date To.
   - Title Uploaded: Displays Filter Date From and Filter Date To.
   - Exporter: Displays a free-type filter field.
   - Buyer: Displays a free-type filter field.
   - Importer Stock # a free-type filter field.
   - VIN: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Created Colum:**

The “Ohio Titles - Needs To Be Sent Outgoing” To Do List card’s “Created Date” value will be in bold and red if current date is ≥ Created Date + 14 days.

**Title Uploaded Colum:**

The “Ohio Titles - Needs To Be Sent Outgoing” To Do List card’s “Title Uploaded” value will be in bold and red if current date is ≥ Title Uploaded + 1 day. “Title Uploaded” is the date the most recent Ohio Title document was associated with the vehicle.

**Edit Action:**

Clicking the "EDIT" text button navigates the user to Vehicle Details – Titles (tab) for that vehicle.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
